import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input from "src/components/inputs/Input";
import ToggleWithLabel from "src/components/toggles/Toggle";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { BUSINESS_TYPE, LEAD_STATUS_OPTIONS } from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateVendor,
  useGetVendorData,
  useUpdateVendor,
} from "../api-vendors";

const FORM_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  PHONE: "phone",
  ADDRESS: "address",
  BUSINESS_TYPE: "type",
  NOTES: "notes",
  VERIFICATION_STATUS: "verifyStatus",
  STATUS: "status",
};

const CreateVendor = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.VENDOR_MANAGEMENT);
  const navigate = useNavigate();
  const { vendorId: id } = useParams();

  const vendorId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!vendorId, [vendorId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetVendorLoading, data } = useGetVendorData(vendorId);

  const vendorData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateVendor,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateVendor(vendorId);

  const { isLoading, isSuccess, execute, error } = useCreateVendor();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: vendorData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.EMAIL]: vendorData?.[FORM_FIELDS.EMAIL] ?? "",
      [FORM_FIELDS.PHONE]: vendorData?.[FORM_FIELDS.PHONE] ?? "",
      [FORM_FIELDS.ADDRESS]: vendorData?.[FORM_FIELDS.ADDRESS] ?? "",
      [FORM_FIELDS.BUSINESS_TYPE]:
        vendorData?.[FORM_FIELDS.BUSINESS_TYPE] ?? null,
      [FORM_FIELDS.NOTES]: vendorData?.[FORM_FIELDS.NOTES] ?? "",
      [FORM_FIELDS.VERIFICATION_STATUS]:
        vendorData?.[FORM_FIELDS.VERIFICATION_STATUS] ?? null,
      [FORM_FIELDS.STATUS]: vendorData?.[FORM_FIELDS.STATUS] ?? null,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.EMAIL]: string()
        .email("Please enter valid email.")
        .required("This is a Required field."),
      [FORM_FIELDS.PHONE]: number().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        delete data.name;
        delete data.email;
        updateVendor(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Vendor Updated" : "Vendor Created."
      );
      navigate("/store/vendor/vendors");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification(error?.cause?.message);
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetVendorLoading || isUpdateLoading,
    [isGetVendorLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Vendor" showBreadcrumb />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <Input
            label="Vendor Name"
            placeholder="Vendor Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isInEditMode || isFieldDisabled}
          />
          <Input
            type="email"
            label="Email"
            placeholder="Email"
            name={FORM_FIELDS.EMAIL}
            value={values[FORM_FIELDS.EMAIL] as string}
            error={getFieldError(FORM_FIELDS.EMAIL)}
            onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
            disabled={isInEditMode || isFieldDisabled}
            required
          />
          <Input
            type="number"
            label="Phone"
            placeholder="Phone"
            name={FORM_FIELDS.PHONE}
            value={values[FORM_FIELDS.PHONE] as string}
            error={getFieldError(FORM_FIELDS.PHONE)}
            onChange={(e) => setFieldValue(FORM_FIELDS.PHONE, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
          <Input
            label="Address"
            placeholder="Address"
            name={FORM_FIELDS.ADDRESS}
            value={values[FORM_FIELDS.ADDRESS] as string}
            error={getFieldError(FORM_FIELDS.ADDRESS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.ADDRESS, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Dropdown
            label="Business Type"
            placeholder="Business Type"
            className={"w-full"}
            options={BUSINESS_TYPE}
            value={values[FORM_FIELDS.BUSINESS_TYPE] as string}
            onChange={(val) => setFieldValue(FORM_FIELDS.BUSINESS_TYPE, val)}
            disabled={isFieldDisabled}
          />
          <Input
            label="Notes"
            placeholder="........"
            name={FORM_FIELDS.NOTES}
            value={values[FORM_FIELDS.NOTES] as string}
            error={getFieldError(FORM_FIELDS.NOTES)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NOTES, e.target.value)}
            disabled={isFieldDisabled}
          />
          <Dropdown
            label="Verification Status"
            placeholder="Verification Status"
            className={"w-full"}
            options={LEAD_STATUS_OPTIONS}
            value={values[FORM_FIELDS.VERIFICATION_STATUS] as string}
            onChange={(val) =>
              setFieldValue(FORM_FIELDS.VERIFICATION_STATUS, val)
            }
            disabled={isFieldDisabled}
          />
          <ToggleWithLabel
            label="Status"
            checked={values[FORM_FIELDS.STATUS] as boolean}
            onToggle={(val) => [setFieldValue(FORM_FIELDS.STATUS, val)]}
            disabled={isFieldDisabled}
            showToggleStatus
          />
        </div>
        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateVendor;
